import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
  FormControl, InputLabel, Select, MenuItem, Collapse,
  ToggleButton, ToggleButtonGroup, useMediaQuery, Tooltip,
  Snackbar, Checkbox, FormControlLabel, FormGroup, Divider, Switch,
  Autocomplete, Slider, SpeedDial, SpeedDialAction, SpeedDialIcon,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Add, Close, Park, FilterList, Warning, Star, Assignment, Map as MapIcon, Satellite, MyLocation, Forest, ArrowBack, Delete, ContentCut, HighlightAlt, Tune } from '@mui/icons-material';
import L from 'leaflet';
import { MapContainer, TileLayer, WMSTileLayer, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import { treesAPI } from '../services/api';
import api from '../services/api';
import { getTreeFilterOptions, invalidateTreeFilterOptions } from '../services/treeFilterOptions';
import { useAuth } from '../contexts/AuthContext';
import { FlaechenLayer, DrawFlaeche, FitToFlaeche } from '../components/FlaechenMapLayers';
import FlaecheFormDialog, { FlaecheForm, emptyFlaecheForm, formToPayload } from '../components/FlaecheFormDialog';
import {
  ZUSTAND_OPTIONS, VITALITAET_OPTIONS, ENTWICKLUNG_OPTIONS, SICHERHEIT_OPTIONS,
  LEBENSERWARTUNG_OPTIONS, KONTROLL_INTERVALL_OPTIONS, ANZAHL_STAEMME_OPTIONS,
  FUNKTION_OPTIONS,
} from '../data/treeFieldOptions';
import 'leaflet/dist/leaflet.css';

// ─────────────────────────────────────────────────────────────────────────────
// MARKER-STYLE — Zustand bestimmt Form/Farbe, Kontroll-Status den inneren Punkt
// ─────────────────────────────────────────────────────────────────────────────

type MarkerShape = 'circle' | 'warning' | 'triangle' | 'stump' | 'group';

// Zustand → Form und Farbe des Außen-Markers
function getZustandStyle(zustand: string | null, hatSchaden: boolean, gefaellt?: boolean, isGroup?: boolean): { shape: MarkerShape; color: string } {
  // Gefällter Baum → Baumstumpf-Symbol (Holzbraun) – Priorität über Gruppe
  if (gefaellt || (zustand && zustand.toLowerCase().includes('gefällt'))) {
    return { shape: 'stump', color: '#6d4c41' };
  }
  // Baumgruppe → mehrere Punkte (FEAT-015), Farbe nach Zustand
  if (isGroup) {
    if (!zustand) return { shape: 'group', color: '#2e7d32' };
    const zg = zustand.toLowerCase();
    if (zg.includes('erheblich')) return { shape: 'group', color: '#e65100' };
    if (zg.includes('leichte')) return { shape: 'group', color: '#f9a825' };
    if (zg.includes('gefahr')) return { shape: 'group', color: '#fbc02d' };
    return { shape: 'group', color: '#2e7d32' };
  }
  if (!zustand) return { shape: 'circle', color: '#78909c' }; // Unbekannt
  const z = zustand.toLowerCase();
  // Abgestorben → schwarzes Dreieck
  if (z.includes('abgestorben')) return { shape: 'triangle', color: '#212121' };
  // Stark geschädigt / Gefahr im Verzug → gelbes Warnschild
  if (z.includes('gefahr') || (z.includes('stark') && (z.includes('geschädigt') || z.includes('geschaedigt')))) return { shape: 'warning', color: '#fbc02d' };
  // Erhebliche Mängel / stärker geschädigt → oranger Punkt
  if (z.includes('erheblich') || z.includes('stärker') || z.includes('staerker')) return { shape: 'circle', color: '#e65100' };
  // Leichte Mängel / leicht geschädigt → gelber Punkt
  if (z.includes('leichte mängel') || z.includes('leichte maengel') || (z.includes('leicht') && (z.includes('geschädigt') || z.includes('geschaedigt')))) return { shape: 'circle', color: '#f9a825' };
  // Gut / gesund → grün (bei Schäden → orange)
  if (z.includes('gut') || z.includes('gesund')) return { shape: 'circle', color: hatSchaden ? '#ff9800' : '#2e7d32' };
  // Fallback
  if (hatSchaden) return { shape: 'circle', color: '#e65100' };
  return { shape: 'circle', color: '#78909c' };
}

// Kontroll-Status anhand letztem Datum + Intervall (Tree-Level mit Default jährlich)
type ControlStatus = 'aktuell' | 'soon' | 'overdue' | 'never';
function getControlStatus(tree: any): ControlStatus {
  if (!tree.letzte_kontrolle) return 'never';
  const intervall = tree.kontroll_intervall || 'jährlich';
  const last = new Date(tree.letzte_kontrolle);
  const next = new Date(last);
  switch (intervall) {
    case 'halbjährlich': next.setMonth(next.getMonth() + 6); break;
    case '2-jährlich':   next.setFullYear(next.getFullYear() + 2); break;
    case '3-jährlich':   next.setFullYear(next.getFullYear() + 3); break;
    default:             next.setFullYear(next.getFullYear() + 1); break; // jährlich
  }
  const now = new Date();
  const daysToNext = Math.floor((next.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  if (daysToNext < 0) return 'overdue';
  if (daysToNext <= 30) return 'soon';
  return 'aktuell';
}

const CONTROL_INNER_COLOR: Record<ControlStatus, string> = {
  aktuell: '#4caf50',  // grün
  soon:    '#fbc02d',  // gelb
  overdue: '#e53935',  // rot
  never:   '#e53935',  // rot
};

// Erzeugt das SVG/HTML für einen Marker (außen = Zustand, innen = Kontroll-Status)
function buildMarkerHtml(style: { shape: MarkerShape; color: string }, controlColor: string): string {
  if (style.shape === 'warning') {
    // Warnschild: gelbes Dreieck mit Ausrufezeichen, Kontroll-Status als Punkt rechts oben
    return `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" style="display:block">
      <polygon points="11,3 19,18 3,18" fill="${style.color}" stroke="#fff" stroke-width="1.2" stroke-linejoin="round"/>
      <text x="11" y="16" text-anchor="middle" font-size="11" font-weight="900" fill="#212121">!</text>
      <circle cx="16.5" cy="5.5" r="3" fill="${controlColor}" stroke="#fff" stroke-width="1"/>
    </svg>`;
  }
  if (style.shape === 'triangle') {
    return `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" style="display:block">
      <polygon points="11,4 18,18 4,18" fill="${style.color}" stroke="#fff" stroke-width="1.2" stroke-linejoin="round"/>
      <circle cx="16.5" cy="5.5" r="3" fill="${controlColor}" stroke="#fff" stroke-width="1"/>
    </svg>`;
  }
  if (style.shape === 'stump') {
    // Baumstumpf: kurzes braunes Rechteck mit Jahresringen (FEAT-014)
    return `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="display:block">
      <ellipse cx="10" cy="14" rx="7" ry="2.5" fill="${style.color}" stroke="#3e2723" stroke-width="1"/>
      <rect x="3" y="9" width="14" height="5" fill="${style.color}" stroke="#3e2723" stroke-width="1"/>
      <ellipse cx="10" cy="9" rx="7" ry="2.5" fill="#8d6e63" stroke="#3e2723" stroke-width="1"/>
      <ellipse cx="10" cy="9" rx="4" ry="1.4" fill="none" stroke="#3e2723" stroke-width="0.7"/>
      <ellipse cx="10" cy="9" rx="2" ry="0.7" fill="none" stroke="#3e2723" stroke-width="0.6"/>
    </svg>`;
  }
  if (style.shape === 'group') {
    // Baumgruppe: drei überlappende Kreise (FEAT-015)
    return `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" style="display:block">
      <circle cx="7" cy="13" r="5" fill="${style.color}" stroke="#fff" stroke-width="1.3" fill-opacity="0.9"/>
      <circle cx="15" cy="13" r="5" fill="${style.color}" stroke="#fff" stroke-width="1.3" fill-opacity="0.9"/>
      <circle cx="11" cy="8" r="5" fill="${style.color}" stroke="#fff" stroke-width="1.3"/>
      <circle cx="11" cy="9" r="2" fill="${controlColor}"/>
    </svg>`;
  }
  // Standard: Doppelfarben-Punkt
  return `<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style="display:block">
    <circle cx="9" cy="9" r="7" fill="${style.color}" stroke="#fff" stroke-width="1.5"/>
    <circle cx="9" cy="9" r="3" fill="${controlColor}"/>
  </svg>`;
}

function getColor(zustand: string | null, hatSchaden: boolean): string {
  return getZustandStyle(zustand, hatSchaden).color;
}

function MarkersLayer({ trees, onTreeClick, colorsEnabled, eigentuemerColors }: { trees: any[]; onTreeClick: (tree: any) => void; colorsEnabled: boolean; eigentuemerColors: Map<string, string> | null }) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  const [useDetailedMarkers, setUseDetailedMarkers] = useState(false);
  // Bounds-Bump zum Trigger eines Re-Renders im SVG-Modus beim Pan/Zoom
  const [boundsTick, setBoundsTick] = useState(0);

  // Zoom-Listener: ab Zoom 15 detaillierte SVG-Marker (Warnschild/Dreieck/Doppelfarbe),
  // bei kleinerem Zoom schnelle Canvas-Punkte. Schwelle hat sich in Tests bewährt.
  // Wenn Farben deaktiviert sind, immer Canvas (schnellster Modus).
  useEffect(() => {
    if (!map) return;
    const onZoom = () => setUseDetailedMarkers((colorsEnabled || !!eigentuemerColors) && map.getZoom() >= 15);
    const onMove = () => setBoundsTick(t => t + 1);
    onZoom();
    map.on('zoomend', onZoom);
    map.on('moveend', onMove);
    return () => { map.off('zoomend', onZoom); map.off('moveend', onMove); };
  }, [map, colorsEnabled, eigentuemerColors]);

  useEffect(() => {
    if (!map) return;
    if (layerRef.current) map.removeLayer(layerRef.current);
    if (trees.length === 0) return;
    const layer = L.layerGroup();
    layerRef.current = layer;

    // Modus: Nach Eigentümer einfärben (Canvas, schnell) – hat Vorrang.
    if (eigentuemerColors) {
      if (useDetailedMarkers) {
        // Reingezoomt: klickbare DOM-Marker (divIcon), auf den Viewport begrenzt.
        const bounds = map.getBounds().pad(0.2);
        let visible = 0; const MAX_DETAILED = 2000;
        for (let i = 0; i < trees.length && visible < MAX_DETAILED; i++) {
          const tree = trees[i];
          if (!tree.latitude || !tree.longitude) continue;
          if (!bounds.contains([tree.latitude, tree.longitude])) continue;
          visible++;
          const e = (tree.eigentuemer || '').trim() || 'Ohne Angabe';
          const col = eigentuemerColors.get(e) || '#78909c';
          const icon = L.divIcon({
            html: `<div style="width:14px;height:14px;border-radius:50%;background:${col};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.35)"></div>`,
            className: 'bw-tree-marker', iconSize: [14, 14], iconAnchor: [7, 7],
          });
          const marker = L.marker([tree.latitude, tree.longitude], { icon, keyboard: false });
          marker.on('click', () => onTreeClick(tree));
          layer.addLayer(marker);
        }
      } else {
        // Herausgezoomt: schnelle Canvas-Punkte.
        if (!rendererRef.current) rendererRef.current = L.canvas({ padding: 0.5 });
        const renderer = rendererRef.current;
        for (let i = 0; i < trees.length; i++) {
          const tree = trees[i];
          if (!tree.latitude || !tree.longitude) continue;
          const e = (tree.eigentuemer || '').trim() || 'Ohne Angabe';
          const col = eigentuemerColors.get(e) || '#78909c';
          const marker = L.circleMarker([tree.latitude, tree.longitude], {
            radius: 5, fillColor: col, color: '#fff', weight: 1, opacity: 1, fillOpacity: 0.9, renderer,
          });
          marker.on('click', () => onTreeClick(tree));
          layer.addLayer(marker);
        }
      }
    } else if (!colorsEnabled) {
      if (!rendererRef.current) rendererRef.current = L.canvas({ padding: 0.5 });
      const renderer = rendererRef.current;
      for (let i = 0; i < trees.length; i++) {
        const tree = trees[i];
        if (!tree.latitude || !tree.longitude) continue;
        const marker = L.circleMarker([tree.latitude, tree.longitude], {
          radius: 5, fillColor: '#2e7d32',
          color: '#fff', weight: 1, opacity: 1, fillOpacity: 0.9, renderer,
        });
        marker.on('click', () => onTreeClick(tree));
        layer.addLayer(marker);
      }
    } else if (useDetailedMarkers) {
      // Detailed: SVG-Marker mit Form (Warnschild/Dreieck) und Doppelfarbe.
      // Nur Marker im Viewport rendern, sonst hängt der Browser bei 8000+ Markern.
      const bounds = map.getBounds().pad(0.2);
      let visible = 0;
      const MAX_DETAILED = 1500;
      for (let i = 0; i < trees.length && visible < MAX_DETAILED; i++) {
        const tree = trees[i];
        if (!tree.latitude || !tree.longitude) continue;
        if (!bounds.contains([tree.latitude, tree.longitude])) continue;
        visible++;
        const hatSchaden = !!(tree.scha_o_1 || tree.schad_o_2);
        const style = getZustandStyle(tree.zustand, hatSchaden, tree.gefaellt, tree.is_baumgruppe);
        const controlColor = CONTROL_INNER_COLOR[getControlStatus(tree)];
        const html = buildMarkerHtml(style, controlColor);
        const size = style.shape === 'circle' ? 18 : 22;
        const icon = L.divIcon({
          html, className: 'bw-tree-marker',
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
        const marker = L.marker([tree.latitude, tree.longitude], { icon, keyboard: false });
        marker.on('click', () => onTreeClick(tree));
        layer.addLayer(marker);
      }
    } else {
      // Fast: Canvas-Marker mit Farben (Zustand außen, Kontroll-Status innen)
      if (!rendererRef.current) rendererRef.current = L.canvas({ padding: 0.5 });
      const renderer = rendererRef.current;
      for (let i = 0; i < trees.length; i++) {
        const tree = trees[i];
        if (!tree.latitude || !tree.longitude) continue;
        const hatSchaden = !!(tree.scha_o_1 || tree.schad_o_2);
        const style = getZustandStyle(tree.zustand, hatSchaden, tree.gefaellt, tree.is_baumgruppe);
        const controlColor = CONTROL_INNER_COLOR[getControlStatus(tree)];
        const outer = L.circleMarker([tree.latitude, tree.longitude], {
          radius: 6, fillColor: style.color,
          color: '#fff', weight: 1.5, opacity: 1, fillOpacity: 0.95, renderer,
        });
        outer.on('click', () => onTreeClick(tree));
        layer.addLayer(outer);
        const inner = L.circleMarker([tree.latitude, tree.longitude], {
          radius: 2.5, fillColor: controlColor,
          color: controlColor, weight: 0, opacity: 1, fillOpacity: 1,
          renderer, interactive: false,
        });
        layer.addLayer(inner);
      }
    }
    layer.addTo(map);
    return () => { if (layerRef.current) map.removeLayer(layerRef.current); };
  }, [map, trees, onTreeClick, useDetailedMarkers, boundsTick, colorsEnabled, eigentuemerColors]);
  return null;
}

function FitBounds({ trees, targetLat, targetLng, targetZoom }: { trees: any[]; targetLat?: number; targetLng?: number; targetZoom?: number }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || trees.length === 0) return;
    if (targetLat && targetLng) { map.setView([targetLat, targetLng], targetZoom || 18); fitted.current = true; return; }
    const points = trees.filter(t => t.latitude && t.longitude).map(t => [t.latitude, t.longitude] as [number, number]);
    if (points.length > 0) { map.fitBounds(L.latLngBounds(points), { padding: [30, 30] }); fitted.current = true; }
  }, [map, trees, targetLat, targetLng, targetZoom]);
  return null;
}

// Pulsierender Ring auf einen bestimmten Baum (bei „Auf Karte anzeigen")
function HighlightMarker({ lat, lng }: { lat?: number; lng?: number }) {
  const map = useMap();
  const ref = useRef<L.Marker | null>(null);
  useEffect(() => {
    if (ref.current) { map.removeLayer(ref.current); ref.current = null; }
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;
    const icon = L.divIcon({
      className: '',
      html: '<div class="bw-tree-highlight"></div>',
      iconSize: [40, 40], iconAnchor: [20, 20],
    });
    ref.current = L.marker([lat, lng], { icon, interactive: false, zIndexOffset: 1000 }).addTo(map);
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [map, lat, lng]);
  return null;
}

function PlacementMode({ active, onPlace }: { active: boolean; onPlace: (lat: number, lng: number) => void }) {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);
  useMapEvents({
    click: (e) => {
      if (!active) return;
      if (markerRef.current) map.removeLayer(markerRef.current);
      const icon = L.divIcon({
        html: '<div style="width:28px;height:28px;background:#4a7c59;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 1.5v11M1.5 7h11" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg></div>',
        iconSize: [28, 28], iconAnchor: [14, 14], className: '',
      });
      markerRef.current = L.marker(e.latlng, { icon }).addTo(map);
      onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  useEffect(() => {
    const container = map.getContainer();
    if (active) {
      container.style.cursor = 'crosshair';
      container.classList.add('placement-mode-active');
    } else {
      container.style.cursor = '';
      container.classList.remove('placement-mode-active');
      if (markerRef.current) { map.removeLayer(markerRef.current); markerRef.current = null; }
    }
    return () => {
      container.style.cursor = '';
      container.classList.remove('placement-mode-active');
    };
  }, [active, map]);
  return null;
}

function AreaCircle({ active, center, radius, onSetCenter }: { active: boolean; center: { lat: number; lng: number } | null; radius: number; onSetCenter: (lat: number, lng: number) => void }) {
  const map = useMap();
  const circleRef = useRef<L.Circle | null>(null);
  useMapEvents({
    click: (e) => { if (active) onSetCenter(e.latlng.lat, e.latlng.lng); },
  });
  useEffect(() => {
    if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    if (active && center) {
      circleRef.current = L.circle([center.lat, center.lng], {
        radius, color: '#c62828', weight: 2, fillColor: '#c62828', fillOpacity: 0.12,
      }).addTo(map);
    }
  }, [active, center, radius, map]);
  useEffect(() => {
    const container = map.getContainer();
    if (active) container.style.cursor = 'crosshair';
    else {
      container.style.cursor = '';
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    }
    return () => { container.style.cursor = ''; };
  }, [active, map]);
  return null;
}

function CoordinatesDisplay() {
  const [coords, setCoords] = useState({ lat: 0, lng: 0 });
  useMapEvents({ mousemove: (e) => setCoords({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return (
    <Box sx={{
      position: 'absolute',
      bottom: 6,
      left: 52,
      zIndex: 500,
      bgcolor: 'background.paper',
      opacity: 0.7,
      px: 0.75, py: 0.2,
      borderRadius: 1,
      boxShadow: 0,
      fontSize: '10px',
      fontFamily: 'monospace',
      color: 'text.secondary',
      pointerEvents: 'none',
    }}>
      Lng: {coords.lng.toFixed(6)} | Lat: {coords.lat.toFixed(6)}
    </Box>
  );
}

// GPS-Ortung: Karte zur User-Position bewegen + Marker setzen
function LocateUser({ trigger, onLocated, onError }: {
  trigger: number;
  onLocated: () => void;
  onError: (msg: string) => void;
}) {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  useEffect(() => {
    if (trigger === 0) return;

    if (!navigator.geolocation) {
      onError('Geolocation wird vom Browser nicht unterstützt');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;

        // Vorherigen Marker/Kreis entfernen
        if (markerRef.current) map.removeLayer(markerRef.current);
        if (circleRef.current) map.removeLayer(circleRef.current);

        // Blauer Punkt für Position
        const userIcon = L.divIcon({
          className: 'user-location-marker',
          html: '<div style="width:18px;height:18px;background:#1976d2;border:3px solid white;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.3),0 2px 6px rgba(0,0,0,0.3)"></div>',
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        });

        markerRef.current = L.marker([latitude, longitude], { icon: userIcon, interactive: false }).addTo(map);
        circleRef.current = L.circle([latitude, longitude], {
          radius: accuracy,
          color: '#1976d2',
          fillColor: '#1976d2',
          fillOpacity: 0.15,
          weight: 1,
          interactive: false,
        }).addTo(map);

        map.setView([latitude, longitude], Math.max(map.getZoom(), 17), { animate: true });
        onLocated();
      },
      (err) => {
        const msg = err.code === 1 ? 'Standortzugriff wurde verweigert'
                  : err.code === 2 ? 'Standort nicht verfügbar'
                  : err.code === 3 ? 'Zeitüberschreitung beim Standortabruf'
                  : 'Standort konnte nicht ermittelt werden';
        onError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    // eslint-disable-next-line
  }, [trigger]);

  return null;
}

// Baut den initialen newTree-State für die nächste Erfassung:
// Behält gemerkte Felder, leert den Rest (besonders die Baumnummer)
function buildTreeForNextEntry(prev: any, remember: Set<string>) {
  const empty = {
    baumnummer: '', baumart: '', ort: '', strasse: '', eigentueme: '',
    grunflache: '',
    zustand: '', vitalitaet: '', entwicklun: '', sicherheit: '', lebenserw: '',
    kontroll_intervall: '', anzahlst: '', bemerkung: '',
    hoehe: '', stdm: '', kdm: '', sthoehe: '',
  };
  const result: any = { ...empty };
  remember.forEach((f) => { if (f !== 'baumnummer' && prev[f] !== undefined) result[f] = prev[f]; });
  return result;
}

// Konfiguration der merkbaren Felder mit hübschen Labels
const REMEMBER_FIELDS: { key: string; label: string }[] = [
  { key: 'baumart',            label: 'Baumart' },
  { key: 'ort',                label: 'Ort' },
  { key: 'strasse',            label: 'Straße' },
  { key: 'eigentueme',         label: 'Eigentümer' },
  { key: 'grunflache',         label: 'Funktion' },
  { key: 'zustand',            label: 'Zustand' },
  { key: 'vitalitaet',         label: 'Vitalität' },
  { key: 'entwicklun',         label: 'Entwicklung' },
  { key: 'sicherheit',         label: 'Sicherheit' },
  { key: 'lebenserw',          label: 'Lebenserwartung' },
  { key: 'kontroll_intervall', label: 'Kontroll-Intervall' },
  { key: 'hoehe',              label: 'Höhe' },
  { key: 'stdm',               label: 'Stammdurchmesser' },
  { key: 'kdm',                label: 'Kronendurchmesser' },
  { key: 'sthoehe',            label: 'Kronenansatz' },
];

// ALKIS-Eigentum: zuschaltbarer Layer. Lädt GeoJSON einmalig vom Backend
// (/api/alkis/eigentum, je Instanz eigene Datei). Zeichnet Punkte als Dots UND
// Polygone als Flurstücks-Umrisse (mit Popup für die Flurstücksnummer, falls vorhanden).
function AlkisLayer({ visible }: { visible: boolean }) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const [data, setData] = useState<any>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (visible && !tried) {
      setTried(true);
      api.get('/alkis/eigentum').then(r => setData(r.data)).catch(() => setData(null));
    }
  }, [visible, tried]);

  useEffect(() => {
    // Eigene Ebene UNTER den Baum-Markern (zIndex < overlayPane 400), damit die
    // ALKIS-Ebene niemals die Klicks auf die Bäume abfängt.
    if (!map.getPane('alkisPane')) {
      map.createPane('alkisPane');
      const p = map.getPane('alkisPane');
      if (p) {
        p.style.zIndex = '350';
        // Ebene ist rein visuell (Flurstücke ansehen) – sie darf NIE Klicks abfangen,
        // damit die Bäume darüber immer anklickbar bleiben.
        p.style.pointerEvents = 'none';
      }
    }
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (!visible || !data?.features?.length) return;
    const renderer = L.canvas({ pane: 'alkisPane', padding: 0.5 });
    // Wichtig: renderer auch für Polygone erzwingen (via any-Cast, da GeoJSONOptions
    // ihn nicht typisiert), sonst landen Polygone auf dem Standard-Canvas ÜBER den
    // Bäumen und fangen die Klicks ab.
    const geoOpts: any = {
      pane: 'alkisPane',
      renderer,
      pointToLayer: (_f: any, latlng: any) => L.circleMarker(latlng, {
        radius: 3, color: '#1b5e20', weight: 1, fillColor: '#43a047', fillOpacity: 0.6,
        pane: 'alkisPane', renderer,
      }),
      style: () => ({ color: '#2e7d32', weight: 1.5, fillColor: '#43a047', fillOpacity: 0.12 }),
      onEachFeature: (feature: any, lyr: any) => {
        // Popup nur für Flächen (Polygone), nicht für tausende Punkte
        if (feature?.geometry?.type === 'Point') return;
        const p = feature?.properties || {};
        const label = p.flurstueck ?? p.flurstuecksnummer ?? p.flur ?? p.fsk ?? p.bezeichnung ?? p.NOTIZ ?? p.uid ?? p.UID;
        if (label != null && String(label).trim() !== '') lyr.bindPopup(`Flurstück: ${label}`);
      },
    };
    layerRef.current = L.geoJSON(data, geoOpts);
    layerRef.current.addTo(map);
    return () => { if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; } };
  }, [data, visible, map]);

  return null;
}

export default function MapView() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { canEdit, isAdmin } = useAuth();
  const [trees, setTrees] = useState<any[]>([]);
  const [allCount, setAllCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTree, setSelectedTree] = useState<any>(null);
  const [mapLayer, setMapLayer] = useState<'street' | 'satellite'>('street');
  const [locateTrigger, setLocateTrigger] = useState(0);
  const [locating, setLocating] = useState(false);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Marker-Farben-Toggle (default: aus, weil bei 8000+ Bäumen viel schneller).
  // Persistiert in sessionStorage, damit Wahl die Navigation überlebt.
  const [colorsEnabled, setColorsEnabled] = useState<boolean>(() => {
    try { return sessionStorage.getItem('bw.map.colors') === 'on'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('bw.map.colors', colorsEnabled ? 'on' : 'off'); } catch {}
  }, [colorsEnabled]);

  // Marker nach Eigentümer einfärben (optional, unabhängig vom Zustand-Farbmodus).
  const [colorByEigentuemer, setColorByEigentuemer] = useState<boolean>(() => {
    try { return sessionStorage.getItem('bw.map.colorOwner') === 'on'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('bw.map.colorOwner', colorByEigentuemer ? 'on' : 'off'); } catch {}
  }, [colorByEigentuemer]);

  // Feste Palette; jedem Eigentümer wird in Reihenfolge des Auftretens eine Farbe zugewiesen.
  const EIGENTUEMER_PALETTE = ['#1565c0','#2e7d32','#e65100','#6a1b9a','#c2185b','#00838f','#f9a825','#4e342e','#546e7a','#ad1457','#33691e','#4527a0'];
  const eigentuemerColorMap = useMemo(() => {
    const m = new Map<string, string>();
    let i = 0;
    for (const t of trees) {
      const e = (t.eigentuemer || '').trim() || 'Ohne Angabe';
      if (!m.has(e)) { m.set(e, EIGENTUEMER_PALETTE[i % EIGENTUEMER_PALETTE.length]); i++; }
    }
    return m;
  }, [trees]);

  // Helper: navigiert zum Baumdetail und gibt aktuellen Karten-Ausschnitt mit,
  // damit der Zurück-Button die Karte wiederherstellen kann.
  const openTreeDetail = useCallback((treeId: string) => {
    const m = mapInstanceRef.current;
    const state: any = { from: 'map' };
    if (m) {
      const c = m.getCenter();
      state.mapLat = c.lat;
      state.mapLng = c.lng;
      state.mapZoom = m.getZoom();
    }
    navigate(`/trees/${treeId}`, { state });
  }, [navigate]);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterZustand, setFilterZustand] = useState('');
  const [filterSchaeden, setFilterSchaeden] = useState(false);
  const [filterMassnahmen, setFilterMassnahmen] = useState(false);
  const [filterKontrollen, setFilterKontrollen] = useState(false);
  const [filterOhneKontrollen, setFilterOhneKontrollen] = useState(false);
  const [filterGefaellt, setFilterGefaellt] = useState(false);

  // Placement
  const [placementMode, setPlacementMode] = useState(false);
  const [placedCoords, setPlacedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTree, setNewTree] = useState<any>({
    baumnummer: '', baumart: '', ort: '', strasse: '', eigentueme: '',
    grunflache: '',
    zustand: '', vitalitaet: '', entwicklun: '', sicherheit: '', lebenserw: '',
    kontroll_intervall: '', anzahlst: '', bemerkung: '',
    hoehe: '', stdm: '', kdm: '', sthoehe: '',
    // Baumgruppen (FEAT-015)
    is_baumgruppe: false, gruppe_anzahl: '', gruppe_arten: [] as string[],
  });

  // Autocomplete-Optionen für Baumart / Ort / Strasse / Eigentümer (zentraler Cache, lädt 1x/Session)
  const [autocompleteOptions, setAutocompleteOptions] = useState<{ baumarten: string[]; orte: string[]; strassen: string[]; eigentuemer: string[] }>({ baumarten: [], orte: [], strassen: [], eigentuemer: [] });
  useEffect(() => {
    getTreeFilterOptions().then(opts => setAutocompleteOptions({
      baumarten: opts.baumarten, orte: opts.orte, strassen: opts.strassen, eigentuemer: opts.eigentuemer,
    }));
  }, []);
  // Welche Felder sollen für die nächste Erfassung gemerkt werden?
  const [rememberFields, setRememberFields] = useState<Set<string>>(new Set());
  // Toast-Meldung nach erfolgreichem Anlegen
  const [snackbar, setSnackbar] = useState('');

  // Waldflächen: Anzeige-Layer + Zeichnen
  const [flaechen, setFlaechen] = useState<any[]>([]);
  const [showFlaechen, setShowFlaechen] = useState(true);
  const [showAlkis, setShowAlkis] = useState(false); // ALKIS-Eigentum (Punkte)
  const [drawMode, setDrawMode] = useState(false);
  // Bereich-Modus (Kreis) für Sammel-Aktionen: als gefällt markieren / löschen
  const [areaMode, setAreaMode] = useState(false);
  const [areaCenter, setAreaCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [areaRadius, setAreaRadius] = useState(60);
  const [areaAction, setAreaAction] = useState<null | 'fell' | 'delete'>(null);
  const [areaConfirmText, setAreaConfirmText] = useState('');
  const [areaSaving, setAreaSaving] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false); // mobiles Aktionsmenü (SpeedDial)
  const [legendOpen, setLegendOpen] = useState(!isMobile); // Anzeige-/Legenden-Panel ein-/ausklappbar
  const [flaecheDialog, setFlaecheDialog] = useState(false);
  const [flaecheForm, setFlaecheForm] = useState<FlaecheForm>({ ...emptyFlaecheForm });
  const [drawnGeometry, setDrawnGeometry] = useState<any>(null);
  const [savingFlaeche, setSavingFlaeche] = useState(false);

  const targetLat = searchParams.get('lat') ? parseFloat(searchParams.get('lat')!) : undefined;
  const targetLng = searchParams.get('lng') ? parseFloat(searchParams.get('lng')!) : undefined;
  const targetZoom = searchParams.get('zoom') ? parseInt(searchParams.get('zoom')!) : undefined;
  const highlightFlaecheId = searchParams.get('flaeche') || undefined;
  const highlightTreeId = searchParams.get('id') || undefined;

  // Bei Aufruf mit ?flaeche=… die Flächen-Ebene sicher einblenden
  useEffect(() => { if (highlightFlaecheId) setShowFlaechen(true); }, [highlightFlaecheId]);

  // Bei Aufruf mit ?id=… (Baum) das Info-Popup einmalig öffnen
  const treePopupShown = useRef(false);
  useEffect(() => {
    if (treePopupShown.current || !highlightTreeId || trees.length === 0) return;
    const t = trees.find((x: any) => x.id === highlightTreeId);
    if (t) { setSelectedTree(t); treePopupShown.current = true; }
  }, [highlightTreeId, trees]);

  const activeFilters = [filterSchaeden, filterMassnahmen, filterKontrollen, filterOhneKontrollen, filterGefaellt, !!filterZustand].filter(Boolean).length;

  const loadTrees = useCallback(() => {
    setLoading(true);
    const params: any = {};
    if (filterZustand) params.zustand = filterZustand;
    if (filterSchaeden) params.hat_schaeden = 'true';
    if (filterMassnahmen) params.hat_massnahmen = 'true';
    if (filterKontrollen) params.hat_kontrollen = 'true';
    if (filterOhneKontrollen) params.ohne_kontrollen = 'true';
    // FEAT-014: Filter "Gefällt anzeigen" – an = alle inkl. gefällter; aus = backend-default (ohne)
    if (filterGefaellt) params.gefaellt = 'all';

    treesAPI.getMap(params)
      .then(res => { setTrees(res.data); if (allCount === 0) setAllCount(res.data.length); setLoading(false); })
      .catch(() => { setError('Kartendaten konnten nicht geladen werden'); setLoading(false); });
  }, [filterZustand, filterSchaeden, filterMassnahmen, filterKontrollen, filterOhneKontrollen, filterGefaellt]);

  useEffect(() => { loadTrees(); }, [loadTrees]);

  const loadFlaechen = useCallback(() => {
    api.get('/flaechen').then(res => setFlaechen(res.data)).catch(() => {});
  }, []);
  useEffect(() => { loadFlaechen(); }, [loadFlaechen]);

  const handleTreeClick = useCallback((tree: any) => { if (!placementMode && !drawMode && !areaMode) setSelectedTree(tree); }, [placementMode, drawMode, areaMode]);

  // ── Bereich-Modus: Bäume im Kreis ermitteln + Sammel-Aktionen ──────────────
  const distM = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const R = 6371000, toRad = (x: number) => x * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };
  const treesInArea = useMemo(() => {
    if (!areaMode || !areaCenter) return [] as any[];
    return trees.filter((t: any) => t.latitude && t.longitude
      && distM(areaCenter, { lat: t.latitude, lng: t.longitude }) <= areaRadius);
  }, [areaMode, areaCenter, areaRadius, trees]);

  const exitAreaMode = () => { setAreaMode(false); setAreaCenter(null); setAreaAction(null); setAreaConfirmText(''); };

  const applyAreaFell = async () => {
    setAreaSaving(true);
    try {
      await api.post('/trees/bulk-fell', { ids: treesInArea.map((t: any) => t.id) });
      exitAreaMode(); loadTrees();
    } catch { setError('Fehler beim Markieren'); }
    finally { setAreaSaving(false); }
  };

  const applyAreaDelete = async () => {
    setAreaSaving(true);
    try {
      await api.post('/trees/bulk-delete', { ids: treesInArea.map((t: any) => t.id) });
      exitAreaMode(); loadTrees();
    } catch { setError('Fehler beim Löschen'); }
    finally { setAreaSaving(false); }
  };
  // Nach Klick auf Karte: Pin setzen, aber NOCH KEIN Formular öffnen.
  // Der User bestätigt den Standort separat über die Bottom-Bar.
  const handlePlace = (lat: number, lng: number) => { setPlacedCoords({ lat, lng }); };
  const cancelPlacement = () => {
    setPlacementMode(false); setPlacedCoords(null); setCreateDialog(false);
    // Beim Abbrechen NICHT die gemerkten Felder leeren, falls der User danach wieder anlegen will
    setNewTree((prev: any) => buildTreeForNextEntry(prev, rememberFields));
  };
  const clearFilters = () => { setFilterSchaeden(false); setFilterMassnahmen(false); setFilterKontrollen(false); setFilterOhneKontrollen(false); setFilterGefaellt(false); setFilterZustand(''); };

  const handleCreateTree = async () => {
    if (!placedCoords || !newTree.baumnummer) return;
    setSaving(true);
    try {
      const payload: any = { ...newTree, latitude: placedCoords.lat, longitude: placedCoords.lng };
      // Numerische Felder leeren, wenn leer (Backend erwartet null)
      ['hoehe', 'stdm', 'kdm', 'sthoehe'].forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      // Baumgruppen-Daten normalisieren (FEAT-015)
      if (payload.is_baumgruppe) {
        payload.gruppe_anzahl = payload.gruppe_anzahl ? parseInt(payload.gruppe_anzahl) : null;
        // Wenn Gruppe: baumart aus den Gruppen-Arten füllen (Komma-Liste, damit Suche/Filter weiter funktionieren)
        if (Array.isArray(payload.gruppe_arten) && payload.gruppe_arten.length > 0) {
          payload.baumart = payload.gruppe_arten.join(', ');
        }
      } else {
        // Bei Einzelbaum die Gruppen-Felder leeren
        payload.gruppe_anzahl = null;
        payload.gruppe_arten = null;
      }
      await treesAPI.create(payload);
      invalidateTreeFilterOptions();

      // Snackbar zeigen
      setSnackbar(`${newTree.is_baumgruppe ? 'Baumgruppe' : 'Baum'} "${newTree.baumnummer}" wurde erfasst`);

      // Dialog + Pin schließen, gemerkte Felder behalten, Karte refreshen
      setCreateDialog(false);
      setPlacementMode(false);
      setPlacedCoords(null);
      setNewTree((prev: any) => buildTreeForNextEntry(prev, rememberFields));
      loadTrees();
      // KEINE Navigation – User bleibt auf der Karte für die nächste Erfassung
    } catch (err: any) { setError(err.response?.data?.error || 'Fehler beim Erstellen'); }
    finally { setSaving(false); }
  };

  // Polygon fertig gezeichnet → Attribut-Dialog öffnen
  const handleDrawComplete = (geojson: any) => {
    setDrawnGeometry(geojson);
    setDrawMode(false);
    setFlaecheForm({ ...emptyFlaecheForm });
    setFlaecheDialog(true);
  };

  const handleSaveFlaeche = async () => {
    if (!flaecheForm.name.trim()) { setSnackbar('Name erforderlich'); return; }
    setSavingFlaeche(true);
    try {
      await api.post('/flaechen', { ...formToPayload(flaecheForm), geometry: drawnGeometry });
      setSnackbar(`Fläche "${flaecheForm.name}" angelegt`);
      setFlaecheDialog(false);
      setDrawnGeometry(null);
      loadFlaechen();
    } catch {
      setSnackbar('Fehler beim Speichern der Fläche');
    } finally {
      setSavingFlaeche(false);
    }
  };

  if (loading && trees.length === 0) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <CircularProgress /><Typography sx={{ ml: 2 }}>Lade Kartendaten...</Typography>
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', height: isMobile ? 'calc(100dvh - 150px)' : 'calc(100vh - 120px)', borderRadius: 2, overflow: 'hidden' }}>
      <style>{`
        .bw-tree-highlight {
          width: 34px; height: 34px; border-radius: 50%;
          border: 3px solid #1565c0;
          box-shadow: 0 0 0 4px rgba(21,101,192,0.25);
          animation: bwpulse 1.4s ease-out infinite;
        }
        @keyframes bwpulse {
          0%   { box-shadow: 0 0 0 0 rgba(21,101,192,0.45); }
          70%  { box-shadow: 0 0 0 16px rgba(21,101,192,0); }
          100% { box-shadow: 0 0 0 0 rgba(21,101,192,0); }
        }
      `}</style>
      {error && !createDialog && <Alert severity="error" sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1400 }} onClose={() => setError('')}>{error}</Alert>}

      <MapContainer ref={mapInstanceRef} center={[51.85, 11.05]} zoom={11} style={{ height: '100%', width: '100%' }} preferCanvas={true} zoomControl={false} maxZoom={21} attributionControl={!isMobile}>
        {mapLayer === 'street' ? (
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={21}
            maxNativeZoom={19}
          />
        ) : (
          <>
            {/* Rückfall-Unterlage: weltweites Esri-Luftbild. Wird sichtbar, wo das
                DOP20 (noch) nicht geladen ist oder der Behörden-WMS mal ausfällt –
                verhindert eine graue Karte. */}
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={21}
              maxNativeZoom={19}
            />
            {/* Darüber das amtliche DOP20 (20 cm) Sachsen-Anhalt, deckend (JPEG) –
                überdeckt Esri dort, wo es lädt. Kostenfrei (DL-DE-BY 2.0). */}
            <WMSTileLayer
              url="https://www.geodatenportal.sachsen-anhalt.de/wss/service/ST_LVermGeo_DOP_WMS_OpenData/guest"
              layers="lsa_lvermgeo_dop20_2"
              format="image/jpeg"
              transparent={false}
              version="1.3.0"
              maxZoom={21}
              attribution='&copy; GeoBasis-DE / LVermGeo ST (DL-DE-BY 2.0)'
            />
          </>
        )}
        <ZoomControl position="bottomleft" />
        <FlaechenLayer flaechen={flaechen} visible={showFlaechen} onFlaecheClick={(id) => navigate(`/flaechen/${id}`)} highlightId={highlightFlaecheId} />
        <AlkisLayer visible={showAlkis} />
        <DrawFlaeche active={drawMode} onComplete={handleDrawComplete} />
        <MarkersLayer trees={trees} onTreeClick={handleTreeClick} colorsEnabled={colorsEnabled}
          eigentuemerColors={colorByEigentuemer ? eigentuemerColorMap : null} />
        {highlightFlaecheId
          ? <FitToFlaeche flaeche={flaechen.find(f => f.id === highlightFlaecheId)} />
          : <FitBounds trees={trees} targetLat={targetLat} targetLng={targetLng} targetZoom={targetZoom} />}
        <HighlightMarker lat={targetLat} lng={targetLng} />
        <PlacementMode active={placementMode} onPlace={handlePlace} />
        <AreaCircle active={areaMode} center={areaCenter} radius={areaRadius}
          onSetCenter={(lat, lng) => setAreaCenter({ lat, lng })} />
        {!isMobile && <CoordinatesDisplay />}
        <LocateUser
          trigger={locateTrigger}
          onLocated={() => setLocating(false)}
          onError={(msg) => { setLocating(false); setError(msg); }}
        />
      </MapContainer>

      {/* Filter Panel - Top Left */}
      <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1000 }}>
        {(highlightTreeId || highlightFlaecheId) && (
          <Button
            variant="contained" size="small" startIcon={<ArrowBack />}
            onClick={() => navigate(highlightTreeId ? `/trees/${highlightTreeId}` : `/flaechen/${highlightFlaecheId}`)}
            sx={{ display: 'flex', mb: 1, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: 'action.hover' } }}
          >
            {highlightTreeId ? 'Zurück zum Baum' : 'Zurück zur Fläche'}
          </Button>
        )}
        <Button variant="contained" size="small" startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ bgcolor: activeFilters > 0 ? 'primary.main' : 'background.paper', color: activeFilters > 0 ? 'white' : 'text.primary', boxShadow: 2, '&:hover': { bgcolor: activeFilters > 0 ? 'primary.dark' : 'action.hover' } }}>
          Filter {activeFilters > 0 && `(${activeFilters})`}
        </Button>
        <Collapse in={showFilters}>
          <Box sx={{ mt: 1, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3, p: 1.5, minWidth: 220 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Chip icon={<Warning sx={{ fontSize: '16px !important' }} />} label="Mit Schäden" size="small"
                variant={filterSchaeden ? 'filled' : 'outlined'} color={filterSchaeden ? 'warning' : 'default'}
                onClick={() => setFilterSchaeden(!filterSchaeden)} sx={{ cursor: 'pointer', justifyContent: 'flex-start' }} />
              <Chip icon={<Star sx={{ fontSize: '16px !important' }} />} label="Offene Maßnahmen" size="small"
                variant={filterMassnahmen ? 'filled' : 'outlined'} color={filterMassnahmen ? 'primary' : 'default'}
                onClick={() => setFilterMassnahmen(!filterMassnahmen)} sx={{ cursor: 'pointer', justifyContent: 'flex-start' }} />
              <Chip icon={<Assignment sx={{ fontSize: '16px !important' }} />} label="Mit Kontrollen" size="small"
                variant={filterKontrollen ? 'filled' : 'outlined'} color={filterKontrollen ? 'success' : 'default'}
                onClick={() => setFilterKontrollen(!filterKontrollen)} sx={{ cursor: 'pointer', justifyContent: 'flex-start' }} />
              <Chip icon={<Assignment sx={{ fontSize: '16px !important' }} />} label="Ohne Kontrollen" size="small"
                variant={filterOhneKontrollen ? 'filled' : 'outlined'} color={filterOhneKontrollen ? 'error' : 'default'}
                onClick={() => setFilterOhneKontrollen(!filterOhneKontrollen)} sx={{ cursor: 'pointer', justifyContent: 'flex-start' }} />
              <Chip label="Gefällte anzeigen" size="small"
                variant={filterGefaellt ? 'filled' : 'outlined'} color={filterGefaellt ? 'warning' : 'default'}
                onClick={() => setFilterGefaellt(!filterGefaellt)} sx={{ cursor: 'pointer', justifyContent: 'flex-start' }} />

              <FormControl size="small" sx={{ mt: 0.5 }}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Zustand</InputLabel>
                <Select value={filterZustand} label="Zustand" onChange={e => setFilterZustand(e.target.value)} sx={{ fontSize: '0.82rem' }}>
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="gesund">Gesund</MenuItem>
                  <MenuItem value="leicht geschädigt">Leicht geschädigt</MenuItem>
                  <MenuItem value="stärker geschädigt">Stärker geschädigt</MenuItem>
                  <MenuItem value="stark geschädigt">Stark geschädigt</MenuItem>
                  <MenuItem value="abgestorben">Abgestorben</MenuItem>
                </Select>
              </FormControl>

              {activeFilters > 0 && (
                <Button size="small" onClick={clearFilters} sx={{ fontSize: '0.75rem' }}>Filter zurücksetzen</Button>
              )}
            </Box>

            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
              {loading ? 'Lade...' : `${trees.length.toLocaleString('de-DE')} von ${allCount.toLocaleString('de-DE')} Bäumen`}
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* Top Right: Layer Switcher + Legend */}
      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
        <ToggleButtonGroup
          value={mapLayer}
          exclusive
          onChange={(_, v) => { if (v) setMapLayer(v); }}
          size="small"
          sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
        >
          <ToggleButton value="street" sx={{ px: 1.3, py: 0.5, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            <MapIcon sx={{ fontSize: 16, mr: 0.5 }} /> Karte
          </ToggleButton>
          <ToggleButton value="satellite" sx={{ px: 1.3, py: 0.5, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            <Satellite sx={{ fontSize: 16, mr: 0.5 }} /> Luftbild
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ bgcolor: 'background.paper', borderRadius: 5, boxShadow: 2 }}>
          <Button size="small" onClick={() => setLegendOpen(o => !o)} startIcon={<Tune sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5, color: 'text.primary', borderRadius: 5 }}>
            Anzeige
          </Button>
        </Box>
        <Collapse in={legendOpen} sx={{ width: '100%' }}>
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 1.5, boxShadow: 2, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 220, ml: 'auto' }}>
          {/* Farben-Toggle (Performance-Hilfe bei vielen Markern) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Farbige Marker</Typography>
            <Switch
              size="small"
              checked={colorsEnabled}
              onChange={(_, checked) => setColorsEnabled(checked)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Nach Eigentümer</Typography>
            <Switch
              size="small"
              checked={colorByEigentuemer}
              onChange={(_, checked) => setColorByEigentuemer(checked)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Waldflächen</Typography>
            <Switch
              size="small"
              checked={showFlaechen}
              onChange={(_, checked) => setShowFlaechen(checked)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Flurstücke (Eigentum)</Typography>
            <Switch
              size="small"
              checked={showAlkis}
              onChange={(_, checked) => setShowAlkis(checked)}
            />
          </Box>
          {colorByEigentuemer && (
            <>
              <Divider sx={{ my: 0.3 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.3 }}>Eigentümer</Typography>
              <Box sx={{ maxHeight: 170, overflowY: 'auto' }}>
                {Array.from(eigentuemerColorMap.entries()).map(([owner, color]) => (
                  <Box key={owner} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }} noWrap>{owner}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
          {!colorsEnabled && !colorByEigentuemer && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', mb: 0.3 }}>
              Alle Bäume als einfache Punkte angezeigt
            </Typography>
          )}
          {colorsEnabled && !colorByEigentuemer && (
            <>
              <Divider sx={{ my: 0.3 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.3 }}>Zustand</Typography>
              {([
                { shape: 'circle',   color: '#2e7d32', label: 'Gut' },
                { shape: 'circle',   color: '#f9a825', label: 'Leichte Mängel' },
                { shape: 'circle',   color: '#e65100', label: 'Erhebliche Mängel' },
                { shape: 'warning',  color: '#fbc02d', label: 'Gefahr im Verzug' },
                { shape: 'triangle', color: '#212121', label: 'Abgestorben' },
                { shape: 'circle',   color: '#78909c', label: 'Unbekannt' },
              ] as { shape: MarkerShape; color: string; label: string }[]).map(({ shape, color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: buildMarkerHtml({ shape, color }, 'transparent') }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.3 }}>Kontrolle</Typography>
              {[
                { color: '#4caf50', label: 'Aktuell' },
                { color: '#fbc02d', label: 'Bald fällig' },
                { color: '#e53935', label: 'Überfällig / nie' },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, border: '1.5px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)' }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
                </Box>
              ))}
            </>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.65rem' }}>{trees.length.toLocaleString('de-DE')} Bäume</Typography>
        </Box>
        </Collapse>
      </Box>

      {/* GPS-Ortung Button (links unten, über Zoom-Controls) */}
      <Box sx={{ position: 'absolute', bottom: 130, left: 10, zIndex: 1000 }}>
        <Tooltip title="Mein Standort" placement="right" arrow>
          <IconButton
            onClick={() => { setLocating(true); setError(''); setLocateTrigger((t) => t + 1); }}
            disabled={locating}
            size="small"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 2,
              width: 36,
              height: 36,
              '&:hover': { bgcolor: 'action.hover' },
              '&.Mui-disabled': { bgcolor: 'background.paper' },
            }}
          >
            {locating
              ? <CircularProgress size={18} sx={{ color: 'primary.main' }} />
              : <MyLocation sx={{ fontSize: 20, color: 'primary.main' }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Bottom bar */}
      {!areaMode && (
      <Box sx={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: '96vw', px: 1 }}>
        {drawMode ? (
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 6, boxShadow: 4, px: 3, py: 1.2, display: 'flex', alignItems: 'center', gap: 2, maxWidth: '90vw' }}>
            <Forest sx={{ color: '#2e7d32', flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              Eckpunkte anklicken · <strong>Doppelklick</strong> zum Abschließen
            </Typography>
            <Button size="small" variant="outlined" color="error" onClick={() => setDrawMode(false)} sx={{ flexShrink: 0 }}>Abbrechen</Button>
          </Box>
        ) : !placementMode ? (
          canEdit && !isMobile && (
            <>
              <Button variant="contained" startIcon={<Add />} onClick={() => { setSelectedTree(null); setPlacementMode(true); }}
                sx={{
                  bgcolor: '#4a7c59', boxShadow: 4, borderRadius: 6, px: isMobile ? 2 : 3,
                  '&:hover': { bgcolor: '#3a6c49' },
                  '& .MuiButton-startIcon': { alignSelf: 'center' },
                }}>
                {isMobile ? 'Baum' : 'Neuen Baum hinzufügen'}
              </Button>
              <Button variant="contained" startIcon={<Forest />} onClick={() => { setSelectedTree(null); setShowFlaechen(true); setDrawMode(true); }}
                sx={{
                  bgcolor: '#2e7d32', boxShadow: 4, borderRadius: 6, px: isMobile ? 2 : 3,
                  '&:hover': { bgcolor: '#1b5e20' },
                  '& .MuiButton-startIcon': { alignSelf: 'center' },
                }}>
                {isMobile ? 'Fläche' : 'Fläche zeichnen'}
              </Button>
              <Button variant="contained" startIcon={<HighlightAlt />} onClick={() => { setSelectedTree(null); setPlacementMode(false); setDrawMode(false); setAreaMode(true); setAreaCenter(null); }}
                sx={{
                  bgcolor: '#8d6e63', boxShadow: 4, borderRadius: 6, px: isMobile ? 2 : 3,
                  '&:hover': { bgcolor: '#6d4c41' },
                  '& .MuiButton-startIcon': { alignSelf: 'center' },
                }}>
                Bereich
              </Button>
            </>
          )
        ) : !placedCoords ? (
          // Schritt 1: Auf Klick warten
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 6, boxShadow: 4, px: 3, py: 1.2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Park sx={{ color: '#4a7c59' }} />
            <Typography variant="body2" fontWeight={600}>Klicke auf die Karte um den Standort zu setzen</Typography>
            <Button size="small" variant="outlined" color="error" onClick={cancelPlacement}>Abbrechen</Button>
          </Box>
        ) : (
          // Schritt 2: Standort gesetzt, bestätigen oder verschieben
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 6, boxShadow: 4, px: 3, py: 1.2, display: 'flex', alignItems: 'center', gap: 2, maxWidth: '90vw' }}>
            <Park sx={{ color: '#4a7c59', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                Standort: {placedCoords.lat.toFixed(6)}, {placedCoords.lng.toFixed(6)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                Tippe auf eine andere Stelle, um den Standort zu verschieben
              </Typography>
            </Box>
            <Button size="small" variant="contained" onClick={() => setCreateDialog(true)}
              sx={{ bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6c49' }, flexShrink: 0 }}>
              Bestätigen
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={cancelPlacement} sx={{ flexShrink: 0 }}>
              Abbrechen
            </Button>
          </Box>
        )}
      </Box>
      )}

      {/* Mobiles Aktionsmenü – ausklappbar, spart Kartenfläche */}
      {isMobile && canEdit && !areaMode && !drawMode && !placementMode && (
        <SpeedDial
          ariaLabel="Karten-Aktionen"
          direction="up"
          open={actionsOpen}
          onOpen={() => setActionsOpen(true)}
          onClose={() => setActionsOpen(false)}
          icon={<SpeedDialIcon />}
          sx={{ position: 'absolute', bottom: 24, right: 20, zIndex: 1000, '& .MuiSpeedDial-fab': { bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6c49' } } }}
        >
          <SpeedDialAction icon={<Add />} tooltipTitle="Neuer Baum" tooltipOpen
            onClick={() => { setActionsOpen(false); setSelectedTree(null); setPlacementMode(true); }} />
          <SpeedDialAction icon={<Forest />} tooltipTitle="Fläche zeichnen" tooltipOpen
            onClick={() => { setActionsOpen(false); setSelectedTree(null); setShowFlaechen(true); setDrawMode(true); }} />
          <SpeedDialAction icon={<HighlightAlt />} tooltipTitle="Bereich" tooltipOpen
            onClick={() => { setActionsOpen(false); setSelectedTree(null); setPlacementMode(false); setDrawMode(false); setAreaMode(true); setAreaCenter(null); }} />
        </SpeedDial>
      )}

      {/* Bereich-Modus: Panel unten */}
      {areaMode && (
        <Box sx={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 6, px: 2.5, py: 1.5, width: 360, maxWidth: '92vw' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            {areaCenter ? `${treesInArea.length} Baum${treesInArea.length === 1 ? '' : 'e'} im Bereich` : 'Auf die Karte klicken, um den Mittelpunkt zu setzen'}
          </Typography>
          {areaCenter && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Radius: {areaRadius} m</Typography>
                <Slider size="small" min={10} max={500} step={5} value={areaRadius}
                  onChange={(_, v) => setAreaRadius(v as number)} sx={{ color: '#c62828' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" color="warning" startIcon={<ContentCut />}
                  disabled={treesInArea.length === 0 || areaSaving}
                  onClick={() => { setAreaConfirmText(''); setAreaAction('fell'); }} sx={{ flex: 1 }}>
                  Als gefällt
                </Button>
                {isAdmin && (
                <Button size="small" variant="contained" color="error" startIcon={<Delete />}
                  disabled={treesInArea.length === 0 || areaSaving}
                  onClick={() => { setAreaConfirmText(''); setAreaAction('delete'); }} sx={{ flex: 1 }}>
                  Löschen
                </Button>
                )}
              </Box>
            </>
          )}
          <Button size="small" variant="text" color="inherit" onClick={exitAreaMode} sx={{ mt: 0.5 }}>Abbrechen</Button>
        </Box>
      )}

      {/* Bereich: „Als gefällt" bestätigen (einfach) */}
      <Dialog open={areaAction === 'fell'} onClose={() => !areaSaving && setAreaAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Als gefällt markieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{treesInArea.length}</strong> Baum{treesInArea.length === 1 ? '' : 'e'} im Bereich werden als <strong>gefällt</strong> markiert,
            erhalten die Nummer <strong>GF-…</strong> (z.B. 0528 → GF-0528) und werden in der Bemerkung als <strong>ehemalig</strong> vermerkt. Fortfahren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAreaAction(null)} disabled={areaSaving}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={applyAreaFell} disabled={areaSaving}>
            {areaSaving ? 'Markiere…' : 'Als gefällt markieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bereich: Löschen bestätigen (doppelt – Tippen von LÖSCHEN) */}
      <Dialog open={areaAction === 'delete'} onClose={() => !areaSaving && setAreaAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#c62828' }}>Bäume endgültig löschen</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <strong>{treesInArea.length}</strong> Baum{treesInArea.length === 1 ? '' : 'e'} im Bereich werden <strong>unwiderruflich</strong> gelöscht.
            Das kann nicht rückgängig gemacht werden.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Zur Bestätigung bitte <strong>LÖSCHEN</strong> eintippen:
          </Typography>
          <TextField size="small" fullWidth autoFocus value={areaConfirmText}
            onChange={(e) => setAreaConfirmText(e.target.value)} placeholder="LÖSCHEN" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAreaAction(null)} disabled={areaSaving}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={applyAreaDelete}
            disabled={areaSaving || areaConfirmText.trim().toUpperCase() !== 'LÖSCHEN'}>
            {areaSaving ? 'Lösche…' : `${treesInArea.length} löschen`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selected tree popup */}
      {selectedTree && !placementMode && (
        <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 4, p: 2, minWidth: 300, maxWidth: 400 }}>
          <IconButton size="small" onClick={() => setSelectedTree(null)} sx={{ position: 'absolute', top: 4, right: 4 }}><Close sx={{ fontSize: 18 }} /></IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: getColor(selectedTree.zustand, !!(selectedTree.scha_o_1 || selectedTree.schad_o_2)), border: '2px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)' }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'primary.main' }}>Baum {selectedTree.baumnummer}</Typography>
          </Box>
          <Typography variant="body2"><strong>Baumart:</strong> {selectedTree.baumart || '–'}</Typography>
          <Typography variant="body2"><strong>Standort:</strong> {[selectedTree.strasse, selectedTree.ort].filter(Boolean).join(', ') || '–'}</Typography>
          <Typography variant="body2"><strong>Zustand:</strong> {selectedTree.zustand || '–'}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {(selectedTree.scha_o_1 || selectedTree.schad_o_2) && <Chip label="Schäden" size="small" color="warning" sx={{ fontSize: '0.7rem' }} />}
            {selectedTree.gefaellt && <Chip label="Gefällt" size="small" sx={{ fontSize: '0.7rem' }} />}
          </Box>
          <Button variant="contained" size="small" fullWidth sx={{ mt: 1.5 }} onClick={() => openTreeDetail(selectedTree.id)}>Details anzeigen</Button>
        </Box>
      )}

      {/* Create Tree Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}><Park /> Neuen Baum anlegen</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {/* Fehlermeldung im Dialog statt im Karten-Layer (sonst läge der Alert hinter dem Dialog) */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {placedCoords && <Alert severity="info" sx={{ mb: 2, fontSize: '0.82rem' }}>Standort: {placedCoords.lat.toFixed(6)}, {placedCoords.lng.toFixed(6)}</Alert>}

          {/* Hinweis-Box: gemerkte Felder */}
          {rememberFields.size > 0 && (
            <Alert severity="warning" icon={false} sx={{ mb: 2, fontSize: '0.82rem', bgcolor: '#fff4e5', color: '#663c00', '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3 }}>
                Folgende Werte werden für die nächste Erfassung übernommen:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(rememberFields).map((f) => {
                  const cfg = REMEMBER_FIELDS.find((x) => x.key === f);
                  return cfg ? <Chip key={f} label={cfg.label} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#ffe0b2', color: '#663c00' }} /> : null;
                })}
              </Box>
            </Alert>
          )}

          {/* Baumgruppen-Toggle (FEAT-015) — ganz oben, ändert das Erfassungs-Modell */}
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: 1.5,
            border: '1px solid', borderColor: newTree.is_baumgruppe ? 'primary.main' : 'divider',
            bgcolor: newTree.is_baumgruppe ? alpha(theme.palette.primary.main, 0.06) : 'action.hover',
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!newTree.is_baumgruppe}
                  onChange={(_, checked) => setNewTree({ ...newTree, is_baumgruppe: checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>Das ist eine Baumgruppe</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    Für mehrere kleine Bäume die dicht beieinanderstehen (Hecke, Strauchgruppe etc.)
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: 'flex-start' }}
            />
            {newTree.is_baumgruppe && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  size="small"
                  type="number"
                  label="Anzahl Bäume *"
                  value={newTree.gruppe_anzahl || ''}
                  onChange={e => setNewTree({ ...newTree, gruppe_anzahl: e.target.value })}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                  inputProps={{ min: 2 }}
                />
                <Autocomplete
                  multiple freeSolo size="small"
                  options={autocompleteOptions.baumarten}
                  value={newTree.gruppe_arten || []}
                  onChange={(_, v) => setNewTree({ ...newTree, gruppe_arten: v as string[] })}
                  sx={{ flex: 1 }}
                  renderTags={(value, getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Arten (mehrere möglich)" placeholder="Eiche, Linde..." />}
                />
              </Box>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Baumnummer *" value={newTree.baumnummer} onChange={e => setNewTree({...newTree, baumnummer: e.target.value})} autoFocus /></Grid>
            {/* Baumart nur bei Einzelbaum, sonst sind Arten in der Gruppen-Box */}
            {!newTree.is_baumgruppe && (
              <Grid item xs={6}>
                <Autocomplete
                  freeSolo size="small"
                  options={autocompleteOptions.baumarten}
                  value={newTree.baumart || ''}
                  onChange={(_, v) => setNewTree({ ...newTree, baumart: v || '' })}
                  onInputChange={(_, v) => setNewTree({ ...newTree, baumart: v })}
                  renderInput={(params) => <TextField {...params} label="Baumart" />}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.orte}
                value={newTree.ort || ''}
                onChange={(_, v) => setNewTree({ ...newTree, ort: v || '' })}
                onInputChange={(_, v) => setNewTree({ ...newTree, ort: v })}
                renderInput={(params) => <TextField {...params} label="Ort" />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.strassen}
                value={newTree.strasse || ''}
                onChange={(_, v) => setNewTree({ ...newTree, strasse: v || '' })}
                onInputChange={(_, v) => setNewTree({ ...newTree, strasse: v })}
                renderInput={(params) => <TextField {...params} label="Straße" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.eigentuemer}
                value={newTree.eigentueme || ''}
                onChange={(_, v) => setNewTree({ ...newTree, eigentueme: v || '' })}
                onInputChange={(_, v) => setNewTree({ ...newTree, eigentueme: v })}
                renderInput={(params) => <TextField {...params} label="Eigentümer" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Funktion</InputLabel>
                <Select label="Funktion" notched value={newTree.grunflache || ''} displayEmpty
                  onChange={e => setNewTree({ ...newTree, grunflache: e.target.value })}>
                  <MenuItem value=""><em>– keine Angabe –</em></MenuItem>
                  {FUNKTION_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Strukturierte Felder als Dropdown */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Zustand</InputLabel>
                <Select value={newTree.zustand} label="Zustand" onChange={e => setNewTree({...newTree, zustand: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ZUSTAND_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Vitalität</InputLabel>
                <Select value={newTree.vitalitaet} label="Vitalität" onChange={e => setNewTree({...newTree, vitalitaet: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {VITALITAET_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Entwicklung</InputLabel>
                <Select value={newTree.entwicklun} label="Entwicklung" onChange={e => setNewTree({...newTree, entwicklun: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ENTWICKLUNG_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Sicherheit</InputLabel>
                <Select value={newTree.sicherheit} label="Sicherheit" onChange={e => setNewTree({...newTree, sicherheit: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {SICHERHEIT_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Lebenserwartung</InputLabel>
                <Select value={newTree.lebenserw} label="Lebenserwartung" onChange={e => setNewTree({...newTree, lebenserw: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {LEBENSERWARTUNG_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Kontroll-Intervall</InputLabel>
                <Select value={newTree.kontroll_intervall} label="Kontroll-Intervall" onChange={e => setNewTree({...newTree, kontroll_intervall: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {KONTROLL_INTERVALL_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Anz. Stämme</InputLabel>
                <Select value={newTree.anzahlst} label="Anz. Stämme" onChange={e => setNewTree({...newTree, anzahlst: e.target.value})}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ANZAHL_STAEMME_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Maße */}
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Höhe (m)" value={newTree.hoehe} onChange={e => setNewTree({...newTree, hoehe: e.target.value})} inputProps={{ min: 0, step: 0.5 }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Stammdurchmesser (cm)" value={newTree.stdm} onChange={e => setNewTree({...newTree, stdm: e.target.value})} inputProps={{ min: 0 }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Kronendurchmesser (m)" value={newTree.kdm} onChange={e => setNewTree({...newTree, kdm: e.target.value})} inputProps={{ min: 0, step: 0.5 }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Kronenansatz (m)" value={newTree.sthoehe} onChange={e => setNewTree({...newTree, sthoehe: e.target.value})} inputProps={{ min: 0, step: 0.5 }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Bemerkung" multiline rows={2} value={newTree.bemerkung} onChange={e => setNewTree({...newTree, bemerkung: e.target.value})} /></Grid>
          </Grid>

          {/* Werte-merken-Sektion */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.8 }}>
            Werte für nächste Erfassung merken
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, fontSize: '0.72rem' }}>
            Wenn du mehrere Bäume mit gleichen Werten erfasst, kannst du hier wählen welche Felder übernommen werden.
          </Typography>
          <FormGroup row sx={{ gap: 0.5 }}>
            {REMEMBER_FIELDS.map((f) => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    size="small"
                    checked={rememberFields.has(f.key)}
                    onChange={(e) => {
                      const next = new Set(rememberFields);
                      if (e.target.checked) next.add(f.key); else next.delete(f.key);
                      setRememberFields(next);
                    }}
                  />
                }
                label={<Typography variant="caption">{f.label}</Typography>}
                sx={{ mr: 0.5 }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleCreateTree}
            disabled={
              saving || !newTree.baumnummer ||
              (newTree.is_baumgruppe && (!newTree.gruppe_anzahl || !(newTree.gruppe_arten?.length > 0)))
            }
            sx={{ bgcolor: '#4a7c59' }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : (newTree.is_baumgruppe ? 'Gruppe anlegen' : 'Baum anlegen')}
          </Button>
          <Button variant="outlined" onClick={() => setCreateDialog(false)}>Zurück</Button>
        </DialogActions>
      </Dialog>

      {/* Flächen-Attribute nach dem Zeichnen erfassen */}
      <FlaecheFormDialog
        open={flaecheDialog}
        onClose={() => { setFlaecheDialog(false); setDrawnGeometry(null); }}
        onSave={handleSaveFlaeche}
        form={flaecheForm}
        setForm={setFlaecheForm}
        saving={savingFlaeche}
        isEdit={false}
      />

      {/* Snackbar nach erfolgreichem Anlegen */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar('')} sx={{ mb: { xs: 8, md: 0 } }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}
